import {useRef, useState} from "react";
import styled from "styled-components";
import {guestErrorCode, guestErrorMessage} from "api/guest.api";
import {TURNSTILE_SITE_KEY} from "conts/features";
import {Turnstile} from "ui/base/turnstile/Turnstile";
import {Alert, AlertIcon, Field, FieldError, Input, Label, PillButton, Spinner, tokens} from "ui/blocks/guestMessaging/GuestUI";
import {CodeEntry} from "./CodeEntry";
import {useCodeLogin} from "./useCodeLogin";
import {formatPhone} from "./GuestDetailsFields";

const looksLikeEmail = (v) => /\S+@\S+\.\S+/.test(v);
const looksLikePhone = (v) => /^\+?1?\D*\d{3}\D*\d{3}\D*\d{4}\D*$/.test(v.trim());

const METHODS = {
    phone: {label: "Phone number", other: "email", switchLabel: "Use email instead", error: "Enter a 10-digit US phone number.", valid: looksLikePhone},
    email: {label: "Email address", other: "phone", switchLabel: "Use phone number instead", error: "Enter a valid email address.", valid: looksLikeEmail},
};

/**
 * Log in with a 4-digit text code instead of a password. Email or phone, then
 * the code sent to the phone confirmed on the account. onSignedIn() runs once
 * the student is signed in; onUsePassword() switches back to the password.
 */
export const CodeLogin = ({initialPhone = "", initialEmail = "", defaultMethod = "phone", source, idPrefix = "cl", onSignedIn, onUsePassword}) => {
    const {sendCode, verify} = useCodeLogin(source);
    // Phone or email; each keeps what was typed when switching.
    const [method, setMethod] = useState(defaultMethod);
    const [values, setValues] = useState({phone: formatPhone(initialPhone), email: initialEmail});
    const identifier = values[method];
    const setIdentifier = (v) => setValues((prev) => ({...prev, [method]: method === "phone" ? formatPhone(v) : v}));
    const m = METHODS[method];
    const [fieldError, setFieldError] = useState("");
    const [error, setError] = useState("");
    const [unavailable, setUnavailable] = useState(false);
    const [busy, setBusy] = useState(false);
    const [last4, setLast4] = useState(null); // set once a code is on its way
    const [turnstileToken, setTurnstileToken] = useState("");
    const [turnstileReset, setTurnstileReset] = useState(0);
    const busyRef = useRef(false);
    const needsBotCheck = Boolean(TURNSTILE_SITE_KEY);

    // Sends (or resends) the code. Returns true when a code is on its way.
    const send = async () => {
        setError("");
        setUnavailable(false);
        try {
            setLast4(await sendCode(identifier, turnstileToken));
            return true;
        } catch (err) {
            const code = guestErrorCode(err);
            if (code === "code_login_unavailable") setUnavailable(true);
            setError(guestErrorMessage(err, "We couldn't send the code. Check your connection and try again."));
            return false;
        } finally {
            setTurnstileReset((n) => n + 1); // Turnstile tokens are single-use
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (busyRef.current) return;
        if (!m.valid(identifier)) {
            setFieldError(m.error);
            return;
        }
        if (needsBotCheck && !turnstileToken) return;
        busyRef.current = true;
        setBusy(true);
        try {
            await send();
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    if (last4 !== null) {
        return (
            <CodeEntry
                phone={last4}
                text="Enter the 4-digit code we sent to the phone on your account."
                confirmLabel="Log in"
                busyLabel="Logging in…"
                changeLabel="Use a different email or phone"
                onVerify={async (code) => {
                    await verify(identifier, code);
                    onSignedIn?.();
                }}
                onResend={send}
                onChangeNumber={() => { setLast4(null); setError(""); }}
            />
        );
    }

    return (
        <Form onSubmit={submit} noValidate aria-busy={busy}>
            <Field>
                <Label htmlFor={`${idPrefix}-identifier`}>
                    {m.label}
                    <SwitchButton
                        type="button"
                        disabled={busy}
                        onClick={() => { setMethod(m.other); setFieldError(""); setError(""); setUnavailable(false); }}
                    >
                        {m.switchLabel}
                    </SwitchButton>
                </Label>
                <Input
                    id={`${idPrefix}-identifier`}
                    key={method}
                    type={method === "phone" ? "tel" : "email"}
                    inputMode={method === "phone" ? "tel" : "email"}
                    autoComplete={method === "phone" ? "tel-national" : "email"}
                    placeholder={method === "phone" ? "(555) 555-5555" : "you@example.com"}
                    value={identifier}
                    disabled={busy}
                    aria-invalid={Boolean(fieldError)}
                    onChange={(e) => { setIdentifier(e.target.value); setFieldError(""); setError(""); setUnavailable(false); }}
                />
                {fieldError && <FieldError>{fieldError}</FieldError>}
            </Field>
            {needsBotCheck && (
                <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setTurnstileToken} resetSignal={turnstileReset}/>
            )}
            {error && <Alert $tone={unavailable ? "warning" : undefined} role="alert"><AlertIcon/><span>{error}</span></Alert>}
            <PillButton type="submit" $block disabled={busy || (needsBotCheck && !turnstileToken)}>
                {busy ? <><Spinner $light $size={16}/> Sending code…</> : "Text me a code"}
            </PillButton>
            {onUsePassword && (
                <TextButton type="button" onClick={onUsePassword} disabled={busy}>Log in with password instead</TextButton>
            )}
        </Form>
    );
};

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 14px;
    font-family: var(--font-primary);
`;

const TextButton = styled.button`
    align-self: center;
    background: none;
    border: 0;
    padding: 4px;
    font: inherit;
    font-size: 14px;
    color: ${tokens.accent};
    text-decoration: underline;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;

const SwitchButton = styled.button`
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: ${tokens.accent};
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
    }
`;
